import { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Slider } from '../ui/slider';
import { Loader2, Sparkles, Mic, MicOff } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AGE_GROUPS = ['U6','U7','U8','U9','U10','U11','U12','U13','U14','U15','U16','U17','U18','Adult'];
const SKILL_LEVELS = [{v:'beginner',l:'Beginner'},{v:'intermediate',l:'Intermediate'},{v:'advanced',l:'Advanced'}];
const FOCUSES = [
  ['passing','Passing'],['finishing','Finishing'],['defending','Defending'],['pressing','Pressing'],
  ['transitions','Transitions'],['set-pieces','Set-pieces'],['possession','Possession'],
  ['1v1','1v1'],['crossing','Crossing'],['shooting','Shooting'],['fitness','Fitness'],
  ['ball-mastery','Ball mastery'],['small-sided-games','Small-sided games'],['goalkeeping','Goalkeeping'],
];
const SURFACES = [['grass','Grass'],['3g','3G'],['indoor','Indoor'],['concrete','Concrete']];
const EQUIPMENT = ['cones','bibs','balls','mannequins','goals','agility-poles','ladders'];

const defaultForm = {
  duration_min: 60,
  player_count: 12,
  age_group: 'U12',
  skill_level: 'intermediate',
  focus_area: 'passing',
  secondary_focus: '',
  equipment_available: ['cones','bibs','balls'],
  surface: 'grass',
  notes: '',
};

export default function AiPlanForm({ onGenerated }) {
  const [form, setForm] = useState(defaultForm);
  const [generating, setGenerating] = useState(false);
  const [recording, setRecording] = useState(false);
  const [voiceLoading, setVoiceLoading] = useState(false);
  const [mediaRecorder, setMediaRecorder] = useState(null);

  // Try to prefill from coach preferences on mount
  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/training/ai/preferences`);
        const p = res.data || {};
        setForm(prev => ({
          ...prev,
          age_group: p.typical_age_group || prev.age_group,
          player_count: p.typical_squad_size || prev.player_count,
          equipment_available: (p.equipment_available && p.equipment_available.length) ? p.equipment_available : prev.equipment_available,
        }));
      } catch (e) { /* ignore */ }
    })();
  }, []);

  const toggleEquipment = (item) => {
    setForm(prev => {
      const has = prev.equipment_available.includes(item);
      return {
        ...prev,
        equipment_available: has
          ? prev.equipment_available.filter(e => e !== item)
          : [...prev.equipment_available, item],
      };
    });
  };

  const handleGenerate = async () => {
    if (form.player_count < 4) {
      toast.error('Please enter at least 4 players');
      return;
    }
    setGenerating(true);
    try {
      const payload = { ...form };
      if (!payload.secondary_focus) delete payload.secondary_focus;
      if (!payload.notes) delete payload.notes;
      const res = await axios.post(`${API}/training/ai/generate`, payload);
      toast.success('Plan generated');
      onGenerated?.(res.data);
    } catch (e) {
      const status = e.response?.status;
      const detail = e.response?.data?.detail || 'Failed to generate plan';
      if (status === 429) toast.error(detail, { duration: 6000 });
      else if (status === 412) toast.error('Please accept the AI plan terms first');
      else toast.error(detail);
    } finally {
      setGenerating(false);
    }
  };

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mr = new MediaRecorder(stream);
      const chunks = [];
      mr.ondataavailable = (e) => { if (e.data.size > 0) chunks.push(e.data); };
      mr.onstop = async () => {
        stream.getTracks().forEach(t => t.stop());
        const blob = new Blob(chunks, { type: 'audio/webm' });
        await processVoice(blob);
      };
      mr.start();
      setMediaRecorder(mr);
      setRecording(true);
    } catch (e) {
      toast.error('Microphone access denied');
    }
  };

  const stopRecording = () => {
    if (mediaRecorder && mediaRecorder.state !== 'inactive') {
      mediaRecorder.stop();
    }
    setRecording(false);
  };

  const processVoice = async (blob) => {
    setVoiceLoading(true);
    try {
      const fd = new FormData();
      fd.append('audio', blob, 'voice.webm');
      fd.append('language', 'en');
      const tx = await axios.post(`${API}/training/ai/transcribe`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      const transcript = tx.data?.transcript;
      if (!transcript) throw new Error('No transcript');
      const parsed = await axios.post(`${API}/training/ai/parse-voice`, { transcript });
      const p = parsed.data || {};
      setForm(prev => ({
        ...prev,
        duration_min: p.duration_min ?? prev.duration_min,
        player_count: p.player_count ?? prev.player_count,
        age_group: p.age_group ?? prev.age_group,
        skill_level: p.skill_level ?? prev.skill_level,
        focus_area: p.focus_area ?? prev.focus_area,
        secondary_focus: p.secondary_focus ?? prev.secondary_focus,
        equipment_available: (p.equipment_available && p.equipment_available.length) ? p.equipment_available : prev.equipment_available,
        surface: p.surface ?? prev.surface,
        notes: p.notes ?? prev.notes,
      }));
      toast.success('Form filled from voice');
    } catch (e) {
      toast.error('Voice processing failed');
    } finally {
      setVoiceLoading(false);
    }
  };

  return (
    <Card data-testid="ai-plan-form">
      <CardHeader>
        <CardTitle className="flex items-center justify-between gap-2">
          <span className="flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-emerald-600" strokeWidth={1.5} />
            Generate a training plan
          </span>
          <Button
            variant={recording ? 'destructive' : 'outline'}
            size="sm"
            onClick={recording ? stopRecording : startRecording}
            disabled={voiceLoading}
            data-testid="ai-plan-voice-btn"
          >
            {voiceLoading ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> :
              recording ? <MicOff className="w-4 h-4 mr-1" /> : <Mic className="w-4 h-4 mr-1" />}
            {voiceLoading ? 'Processing...' : recording ? 'Stop' : 'Voice fill'}
          </Button>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-5">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <Label>Session duration (min): <span className="font-bold text-emerald-700">{form.duration_min}</span></Label>
            <Slider min={20} max={120} step={5} value={[form.duration_min]} onValueChange={(v) => setForm({...form, duration_min: v[0]})} className="mt-3" data-testid="ai-plan-duration" />
          </div>
          <div>
            <Label>Number of players</Label>
            <Input type="number" min={4} max={40} value={form.player_count} onChange={(e) => setForm({...form, player_count: parseInt(e.target.value, 10) || 0})} data-testid="ai-plan-players" />
          </div>
          <div>
            <Label>Age group</Label>
            <Select value={form.age_group} onValueChange={(v) => setForm({...form, age_group: v})}>
              <SelectTrigger data-testid="ai-plan-age"><SelectValue /></SelectTrigger>
              <SelectContent>{AGE_GROUPS.map(a => <SelectItem key={a} value={a}>{a}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label>Skill level</Label>
            <Select value={form.skill_level} onValueChange={(v) => setForm({...form, skill_level: v})}>
              <SelectTrigger data-testid="ai-plan-skill"><SelectValue /></SelectTrigger>
              <SelectContent>{SKILL_LEVELS.map(s => <SelectItem key={s.v} value={s.v}>{s.l}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label>Primary focus</Label>
            <Select value={form.focus_area} onValueChange={(v) => setForm({...form, focus_area: v})}>
              <SelectTrigger data-testid="ai-plan-focus"><SelectValue /></SelectTrigger>
              <SelectContent>{FOCUSES.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label>Secondary focus (optional)</Label>
            <Select value={form.secondary_focus || 'none'} onValueChange={(v) => setForm({...form, secondary_focus: v === 'none' ? '' : v})}>
              <SelectTrigger data-testid="ai-plan-secondary"><SelectValue placeholder="None" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">None</SelectItem>
                {FOCUSES.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Surface</Label>
            <Select value={form.surface} onValueChange={(v) => setForm({...form, surface: v})}>
              <SelectTrigger data-testid="ai-plan-surface"><SelectValue /></SelectTrigger>
              <SelectContent>{SURFACES.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        </div>

        <div>
          <Label>Equipment available</Label>
          <div className="flex flex-wrap gap-2 mt-2" data-testid="ai-plan-equipment">
            {EQUIPMENT.map(eq => {
              const active = form.equipment_available.includes(eq);
              return (
                <button
                  key={eq}
                  type="button"
                  onClick={() => toggleEquipment(eq)}
                  className={`px-3 py-1.5 rounded-full text-sm border transition-all ${
                    active
                      ? 'bg-emerald-500 text-white border-emerald-500'
                      : 'bg-white text-gray-700 border-gray-300 hover:border-emerald-400'
                  }`}
                  data-testid={`ai-plan-equipment-${eq}`}
                >
                  {eq}
                </button>
              );
            })}
          </div>
        </div>

        <div>
          <Label>Coach notes (optional)</Label>
          <Textarea
            rows={2}
            value={form.notes}
            onChange={(e) => setForm({...form, notes: e.target.value})}
            placeholder="e.g. wet pitch, two new starters, working on Saturday's set-pieces..."
            data-testid="ai-plan-notes"
          />
        </div>

        <div className="flex justify-end">
          <Button
            onClick={handleGenerate}
            disabled={generating}
            className="bg-emerald-600 hover:bg-emerald-700 text-white"
            data-testid="ai-plan-generate-btn"
          >
            {generating ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Generating (~15s)...</> :
              <><Sparkles className="w-4 h-4 mr-2" />Generate Plan</>}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
